import React, { useState } from 'react'
import { Alert, Box, Skeleton, Typography } from '@mui/material'
import { useQuery, gql } from '@apollo/client'
import { restaurantReport } from '../apollo'
import { C, FONT, cardSx, cssVars, tabular } from '../components/RestaurantDashboard/tokens'
import { DEFAULT_TZ, addDays, count, dayShort, downloadCsv, money, todayIn } from '../components/RestaurantDashboard/format'
import FilterBar, { PRESETS } from '../components/RestaurantDashboard/FilterBar'

/**
 * The restaurant's sales and payout report.
 *
 * Every figure and every label comes from ONE query, restaurantReport
 * (rushmyfood-backend/src/utils/restaurantReport.js) — the same one the
 * restaurant app's Reports screen and the emailed report use, so all three
 * read the same. The page only picks the range and draws the sections.
 */
const RESTAURANT_REPORT = gql`${restaurantReport}`

const rangeFor = (key, today) => {
  const p = PRESETS.find(x => x.key === key)
  return { from: addDays(today, -((p ? p.days : 7) - 1)), to: today }
}

const signed = (row) => {
  if (row.kind === 'count') return count(row.count)
  if (row.kind === 'minus') return `−${money(Math.abs(row.amount || 0))}`
  return (row.amount || 0) < 0 ? `−${money(Math.abs(row.amount))}` : money(row.amount)
}

function ReportRow({ row }) {
  const isInfo = row.kind === 'info'
  const isGrand = row.kind === 'grand'
  const isTotal = row.kind === 'total' || row.kind === 'subtotal'
  return (
    <Box sx={{
      display: 'flex', alignItems: 'baseline', gap: 2,
      py: isGrand ? 1.5 : 1,
      pl: isInfo ? 2 : 0,
      borderTop: isTotal || isGrand ? `1px solid ${isGrand ? C.text : C.border}` : 'none',
      mt: isGrand ? 0.5 : 0
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: isGrand ? 16 : 14,
          fontWeight: isGrand || row.kind === 'total' ? 700 : row.kind === 'subtotal' ? 600 : 400,
          color: isInfo ? C.textMuted : C.text
        }}>
          {row.label}
        </Typography>
        {row.hint && <Typography sx={{ fontSize: 12, color: C.textFaint, mt: 0.25 }}>{row.hint}</Typography>}
      </Box>
      <Typography sx={{
        ...tabular,
        whiteSpace: 'nowrap',
        fontSize: isGrand ? 20 : 14,
        fontWeight: isGrand || row.kind === 'total' ? 700 : row.kind === 'subtotal' ? 600 : 500,
        color: isGrand ? C.brandText : row.kind === 'minus' ? C.textMuted : isInfo ? C.textMuted : C.text
      }}>
        {signed(row)}
      </Typography>
    </Box>
  )
}

function Section({ section }) {
  return (
    <Box component="section" aria-labelledby={`rpt-${section.key}`} sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
      <Typography id={`rpt-${section.key}`} component="h2" sx={{ fontSize: 16, fontWeight: 700, color: C.text }}>
        {section.title}
      </Typography>
      {section.subtitle && <Typography sx={{ fontSize: 13, color: C.textMuted, mt: 0.25, mb: 1 }}>{section.subtitle}</Typography>}
      <Box sx={{ mt: section.subtitle ? 0 : 1 }}>
        {section.rows.length
          ? section.rows.map((row, i) => <ReportRow key={`${row.label}-${i}`} row={row} />)
          : <Typography sx={{ fontSize: 14, color: C.textMuted, py: 1 }}>Nothing in this period.</Typography>}
      </Box>
    </Box>
  )
}

function Tile({ label, value, strong }) {
  return (
    <Box sx={{ ...cardSx, p: 2, borderColor: strong ? C.brand : C.border }}>
      <Typography sx={{ fontSize: 13, color: C.textMuted }}>{label}</Typography>
      <Typography sx={{ ...tabular, fontSize: 24, fontWeight: 700, color: strong ? C.brandText : C.text, mt: 0.5 }}>{value}</Typography>
    </Box>
  )
}

function DayTable({ days }) {
  const cols = [
    ['Date', d => dayShort(d.date), 'left'],
    ['Orders', d => count(d.orders)],
    ['Food sales', d => money(d.foodSales)],
    ['Customers paid', d => money(d.customersPaid)],
    ['Commission', d => money(d.commission)],
    ['Your share', d => money(d.yourShare)],
    ['RushMyFood sends you', d => (d.rmfSends < 0 ? `−${money(Math.abs(d.rmfSends))}` : money(d.rmfSends))]
  ]
  return (
    <Box component="section" aria-labelledby="rpt-days" sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
      <Typography id="rpt-days" component="h2" sx={{ fontSize: 16, fontWeight: 700, color: C.text, mb: 1 }}>Day by day</Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
          <thead>
            <tr>
              {cols.map(([h, , align]) => (
                <Box component="th" key={h} scope="col" sx={{ textAlign: align || 'right', fontSize: 12, fontWeight: 600, color: C.textMuted, py: 1, px: 1, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {days.map(d => (
              <tr key={d.date}>
                {cols.map(([h, f, align]) => (
                  <Box component="td" key={h} sx={{ ...tabular, textAlign: align || 'right', fontSize: 14, color: C.text, py: 1, px: 1, borderBottom: `1px solid ${C.grid}`, whiteSpace: 'nowrap' }}>{f(d)}</Box>
                ))}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
    </Box>
  )
}

const RestaurantReports = () => {
  const restaurantId = localStorage.getItem('restaurantId')
  const storedName = localStorage.getItem('restaurantName')
  const today = todayIn(DEFAULT_TZ)
  const [preset, setPreset] = useState('7')
  const [range, setRange] = useState(() => rangeFor('7', today))

  const { data: fresh, previousData, loading, error } = useQuery(RESTAURANT_REPORT, {
    variables: { restaurantId, from: range.from, to: range.to },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  })
  const report = (fresh || previousData)?.restaurantReport

  const onExport = () => {
    if (!report) return
    const rows = [['Section', 'Line', 'Amount', 'Count', 'Note']]
    for (const s of report.sections) {
      for (const r of s.rows) {
        const amount = r.amount == null ? '' : (r.kind === 'minus' ? -Math.abs(r.amount) : r.amount).toFixed(2)
        rows.push([s.title, r.label, amount, r.count == null ? '' : r.count, r.hint || ''])
      }
    }
    rows.push([])
    rows.push(['Date', 'Orders', 'Food sales', 'Customers paid', 'Commission', 'Your share', 'RushMyFood sends you'])
    for (const d of report.days) {
      rows.push([d.date, d.orders, d.foodSales.toFixed(2), d.customersPaid.toFixed(2), d.commission.toFixed(2), d.yourShare.toFixed(2), d.rmfSends.toFixed(2)])
    }
    downloadCsv(`report-${range.from}-to-${range.to}.csv`, rows)
  }

  const byKey = key => report?.sections.find(s => s.key === key)
  const first = ['SALES', 'PAYOUT'].map(byKey).filter(Boolean)
  const rest = report ? report.sections.filter(s => !['SALES', 'PAYOUT'].includes(s.key)) : []

  if (!restaurantId) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Choose a restaurant to see its reports.</Alert></Box>
  }

  return (
    <Box sx={{ ...cssVars, fontFamily: FONT, bgcolor: C.page, minHeight: '100vh', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography component="h1" sx={{ fontSize: 24, fontWeight: 700, color: C.text }}>Reports</Typography>
        <Typography sx={{ fontSize: 14, color: C.textMuted, mt: 0.25 }}>
          {report?.restaurantName || storedName || ''}{report ? ` · days in ${report.timezone}` : ''}
        </Typography>
      </Box>

      <FilterBar
        preset={preset}
        from={range.from}
        to={range.to}
        today={today}
        onPreset={key => { setPreset(key); setRange(rangeFor(key, today)) }}
        onRange={(from, to) => { setPreset('custom'); setRange({ from, to }) }}
        onExport={onExport}
        exportDisabled={!report}
      />

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error.message.replace(/^GraphQL error: /, '')}</Alert>}

      {!report && loading && (
        <Box sx={{ display: 'grid', gap: 2, mt: 2 }}>
          <Skeleton variant="rounded" height={96} />
          <Skeleton variant="rounded" height={420} />
        </Box>
      )}

      {report && (
        <Box sx={{ display: 'grid', gap: 2, mt: 2, gridTemplateColumns: 'minmax(0, 1fr)', opacity: loading ? 0.6 : 1, transition: 'opacity 160ms ease' }}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
            <Tile label="Orders" value={count(report.summary.orders)} />
            <Tile label="Food sales" value={money(report.summary.foodSales)} />
            <Tile label="Customers paid" value={money(report.summary.customersPaid)} />
            <Tile label="RushMyFood sends you" value={report.summary.rmfSends < 0 ? `−${money(Math.abs(report.summary.rmfSends))}` : money(report.summary.rmfSends)} strong />
          </Box>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' }, alignItems: 'start' }}>
            {first.map(s => <Section key={s.key} section={s} />)}
          </Box>

          {report.days.length > 0 && <DayTable days={report.days} />}

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, alignItems: 'start' }}>
            {rest.map(s => <Section key={s.key} section={s} />)}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default RestaurantReports
